class SkReelCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.abortController = new AbortController();

    this.viewport = this.querySelector('[data-reel-viewport]');
    this.track = this.querySelector('[data-reel-track]');
    this.cards = Array.from(this.querySelectorAll('[data-reel-card]'));
    this.previousButton = this.querySelector('[data-reel-previous]');
    this.nextButton = this.querySelector('[data-reel-next]');
    this.status = this.querySelector('[data-reel-status]');
    this.interval = Number(this.dataset.interval) || 3000;
    this.duration = Number(this.dataset.duration) || 650;
    this.pauseOnHover = this.dataset.pauseHover === 'true';
    this.autoplayReels = this.dataset.autoplayReels === 'true';
    this.enableMobileSwipe = this.dataset.enableMobileSwipe !== 'false';
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isSectionVisible = true;
    this.isAnimating = false;
    this.pauseReasons = new Set();
    this.suppressClickUntil = 0;

    if (!this.viewport || !this.track || !this.cards.length) return;

    this.classList.add('is-enhanced');
    this.style.setProperty('--skr-transition-duration', `${this.duration}ms`);
    this.cards.forEach((card) => {
      card.querySelectorAll('a').forEach((link) => {
        link.draggable = false;
      });
    });

    this.setupNavigation(this.abortController.signal);
    this.setupMedia(this.abortController.signal);
    this.setupVisibility();
    this.setupInteraction(this.abortController.signal);
    this.setupMobileGestures(this.abortController.signal);
    requestAnimationFrame(() => this.measure());

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.viewport);
    }
  }

  disconnectedCallback() {
    this.clearTimer();
    if (this.animationTimer) window.clearTimeout(this.animationTimer);
    if (this.snapTimer) window.clearTimeout(this.snapTimer);
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.mediaObserver?.disconnect();
    this.sectionObserver?.disconnect();
    this.querySelectorAll('[data-reel-video]').forEach((video) => {
      video.pause();
      video.muted = true;
    });
    this.initialized = false;
  }

  measure() {
    const firstCard = this.cards[0];
    if (!firstCard || !this.viewport || !this.track || this.isAnimating || this.gesture) return;

    const cardWidth = firstCard.getBoundingClientRect().width;
    const trackStyles = getComputedStyle(this.track);
    const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
    const viewportWidth = this.viewport.clientWidth;
    const visibleCards = Math.max(1, Math.floor(viewportWidth / (cardWidth + gap)));
    const mobileCenterIndex = window.matchMedia('(max-width: 749px)').matches && this.cards.length >= 3 ? 1 : 0;
    const centerIndex = Math.min(
      Math.floor((this.cards.length - 1) / 2),
      Math.max(mobileCenterIndex, Math.floor(visibleCards / 2))
    );

    this.cardWidth = cardWidth;
    this.step = cardWidth + gap;
    this.baseX = viewportWidth / 2 - cardWidth / 2 - centerIndex * this.step;
    this.arcDepth = Number.parseFloat(getComputedStyle(this).getPropertyValue('--skr-arc-depth')) || 0;

    this.track.style.transition = 'none';
    this.track.style.transform = `translate3d(${this.baseX}px, 0, 0)`;
    this.updateArc(this.cards, 0, false);
    this.track.getBoundingClientRect();
    this.track.style.removeProperty('transition');
    this.schedule();
  }

  updateArc(cards, shift, animate = true) {
    const viewportCenter = this.viewport.clientWidth / 2;
    const radius = Math.max(viewportCenter, 1);

    cards.forEach((card, index) => {
      const center = this.baseX + index * this.step + this.cardWidth / 2 + shift;
      const distance = Math.min(Math.abs(center - viewportCenter) / radius, 1);
      const curve = 1 - Math.pow(distance, 1.55);
      const y = Math.max(0, this.arcDepth * curve);
      const scale = 0.965 + curve * 0.035;

      card.style.transitionDuration = animate ? `${this.duration}ms` : '0ms';
      card.style.setProperty('--skr-arc-y', `${y.toFixed(2)}px`);
      card.style.setProperty('--skr-arc-scale', scale.toFixed(4));
    });
  }

  setupNavigation(signal) {
    const disabled = this.cards.length < 2;
    if (this.previousButton) this.previousButton.disabled = disabled;
    if (this.nextButton) this.nextButton.disabled = disabled;

    this.previousButton?.addEventListener('click', () => this.move(-1, true), { signal });
    this.nextButton?.addEventListener('click', () => this.move(1, true), { signal });
  }

  advance() {
    this.move(1, false);
  }

  move(direction, userInitiated = false, startShift = 0) {
    if (this.isAnimating || this.cards.length < 2) return false;
    if (!userInitiated && (this.isPaused || !this.isSectionVisible || this.reduceMotion)) {
      this.schedule();
      return false;
    }

    this.clearTimer();
    this.isAnimating = true;
    if (direction > 0) this.moveForward(startShift, userInitiated);
    else this.moveBackward(startShift, userInitiated);
    return true;
  }

  moveForward(startShift, userInitiated) {
    const firstCard = this.cards[0];
    const visualClone = this.createVisualClone(firstCard);
    this.track.append(visualClone);

    const movingCards = [...this.cards, visualClone];
    this.track.style.transition = 'none';
    this.track.style.transform = `translate3d(${this.baseX + startShift}px, 0, 0)`;
    this.updateArc(movingCards, startShift, false);
    this.track.getBoundingClientRect();
    this.updateArc(movingCards, -this.step, true);
    this.track.style.transition = `transform ${this.duration}ms cubic-bezier(0.22, 1, 0.36, 1)`;
    this.track.style.transform = `translate3d(${this.baseX - this.step}px, 0, 0)`;

    this.animationTimer = window.setTimeout(() => {
      visualClone.remove();
      this.track.append(firstCard);
      this.cards.push(this.cards.shift());
      this.resetAfterMove(userInitiated, 'Showing next reels.');
    }, this.duration);
  }

  moveBackward(startShift, userInitiated) {
    const lastCard = this.cards[this.cards.length - 1];
    const visualClone = this.createVisualClone(lastCard);
    this.track.insertBefore(visualClone, this.track.firstElementChild);

    const movingCards = [visualClone, ...this.cards];
    this.track.style.transition = 'none';
    this.track.style.transform = `translate3d(${this.baseX - this.step + startShift}px, 0, 0)`;
    this.updateArc(movingCards, -this.step + startShift, false);
    this.track.getBoundingClientRect();
    this.updateArc(movingCards, 0, true);
    this.track.style.transition = `transform ${this.duration}ms cubic-bezier(0.22, 1, 0.36, 1)`;
    this.track.style.transform = `translate3d(${this.baseX}px, 0, 0)`;

    this.animationTimer = window.setTimeout(() => {
      visualClone.remove();
      this.track.insertBefore(lastCard, this.track.firstElementChild);
      this.cards.unshift(this.cards.pop());
      this.resetAfterMove(userInitiated, 'Showing previous reels.');
    }, this.duration);
  }

  createVisualClone(card) {
    const clone = card.cloneNode(true);
    clone.classList.remove('is-playing', 'is-audible');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('data-reel-card');
    clone.removeAttribute('data-shopify-editor-block');
    clone.querySelectorAll('video, iframe, [data-reel-sound]').forEach((item) => item.remove());
    clone.querySelectorAll('a, button, [tabindex]').forEach((item) => item.setAttribute('tabindex', '-1'));
    return clone;
  }

  resetAfterMove(userInitiated, message) {
    this.track.style.transition = 'none';
    this.track.style.transform = `translate3d(${this.baseX}px, 0, 0)`;
    this.updateArc(this.cards, 0, false);
    this.track.getBoundingClientRect();
    this.track.style.removeProperty('transition');
    this.cards.forEach((card) => card.style.removeProperty('transition-duration'));
    this.isAnimating = false;
    this.animationTimer = undefined;
    if (userInitiated && this.status) this.status.textContent = message;
    this.schedule();
  }

  setupMedia(signal) {
    this.videos = Array.from(this.querySelectorAll('[data-reel-video]'));
    this.videos.forEach((video) => {
      video.muted = true;
      video.defaultMuted = true;
      this.updateSoundButton(video);
    });

    this.querySelectorAll('[data-reel-sound]').forEach((button) => {
      button.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.toggleSound(button);
        },
        { signal }
      );
    });

    if (!this.videos.length || !('IntersectionObserver' in window)) return;

    this.mediaObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          const card = video.closest('[data-reel-card]');

          if (entry.isIntersecting && this.autoplayReels && !this.reduceMotion) {
            video.play().then(() => card?.classList.add('is-playing')).catch(() => {});
          } else if (!entry.isIntersecting) {
            video.pause();
            if (!video.muted) this.setVideoMuted(video, true);
          }
        });
      },
      { root: this.viewport, rootMargin: '0px 8%', threshold: 0.25 }
    );

    this.videos.forEach((video) => this.mediaObserver.observe(video));
  }

  toggleSound(button) {
    const card = button.closest('[data-reel-card]');
    const video = card?.querySelector('[data-reel-video]');
    if (!video) return;

    if (video.muted) {
      this.videos.forEach((otherVideo) => {
        if (otherVideo !== video) this.setVideoMuted(otherVideo, true);
      });
      video.muted = false;
      this.audibleVideo = video;
      this.updateSoundButton(video);
      this.pause('sound');
      video.play().then(() => card.classList.add('is-playing')).catch(() => this.setVideoMuted(video, true));
    } else {
      this.setVideoMuted(video, true);
    }
  }

  setVideoMuted(video, muted) {
    video.muted = muted;
    this.updateSoundButton(video);
    if (muted && this.audibleVideo === video) {
      this.audibleVideo = null;
      this.resume('sound');
    }
  }

  updateSoundButton(video) {
    const card = video.closest('[data-reel-card]');
    const button = card?.querySelector('[data-reel-sound]');
    if (!button) return;

    const soundOn = !video.muted;
    const label = soundOn ? 'Turn sound off' : 'Turn sound on';
    button.setAttribute('aria-pressed', String(soundOn));
    button.setAttribute('aria-label', label);
    button.title = label;
    card.classList.toggle('is-audible', soundOn);
  }

  setupVisibility() {
    if (!('IntersectionObserver' in window)) {
      this.schedule();
      return;
    }

    this.sectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.isSectionVisible = entry.isIntersecting;
        if (this.isSectionVisible) this.schedule();
        else this.clearTimer();
      },
      { threshold: 0.12 }
    );
    this.sectionObserver.observe(this);
  }

  setupInteraction(signal) {
    if (this.pauseOnHover) {
      this.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType === 'mouse') this.pause('hover');
        },
        { signal }
      );
      this.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType === 'mouse') this.resume('hover');
        },
        { signal }
      );
    }

    this.addEventListener('focusin', () => this.pause('focus'), { signal });
    this.addEventListener(
      'focusout',
      (event) => {
        if (!this.contains(event.relatedTarget)) this.resume('focus');
      },
      { signal }
    );

    this.addEventListener(
      'pointerover',
      (event) => {
        if (this.autoplayReels || event.pointerType !== 'mouse') return;
        const card = event.target.closest('[data-reel-card]');
        const video = card?.querySelector('[data-reel-video]');
        if (!video) return;
        video.play().then(() => card.classList.add('is-playing')).catch(() => {});
      },
      { signal }
    );

    this.addEventListener(
      'pointerout',
      (event) => {
        if (this.autoplayReels || event.pointerType !== 'mouse') return;
        const card = event.target.closest('[data-reel-card]');
        if (card?.contains(event.relatedTarget)) return;
        card?.querySelector('[data-reel-video]')?.pause();
      },
      { signal }
    );

    this.addEventListener(
      'click',
      (event) => {
        if (performance.now() < this.suppressClickUntil && event.target.closest('.skr-card__link')) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      { capture: true, signal }
    );
  }

  setupMobileGestures(signal) {
    if (!this.enableMobileSwipe || !('PointerEvent' in window)) return;

    this.viewport.addEventListener(
      'pointerdown',
      (event) => {
        if (!window.matchMedia('(max-width: 749px)').matches || event.pointerType === 'mouse' || !event.isPrimary) return;
        if (this.isAnimating || !Number.isFinite(this.step)) return;
        if (event.target.closest('[data-reel-sound]')) return;

        this.gesture = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          startTime: performance.now(),
          delta: 0,
          dragging: false,
        };
        this.pause('gesture');
        try {
          this.viewport.setPointerCapture(event.pointerId);
        } catch (_error) {}
      },
      { signal }
    );

    this.viewport.addEventListener(
      'pointermove',
      (event) => {
        if (!this.gesture || event.pointerId !== this.gesture.pointerId || this.isAnimating) return;

        const deltaX = event.clientX - this.gesture.startX;
        const deltaY = event.clientY - this.gesture.startY;
        if (!this.gesture.dragging && Math.abs(deltaX) > 7 && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.gesture.dragging = true;
        }
        if (!this.gesture.dragging) return;

        event.preventDefault();
        const limit = Math.max(60, this.step * 0.78);
        this.gesture.delta = Math.max(-limit, Math.min(limit, deltaX));
        this.track.style.transition = 'none';
        this.track.style.transform = `translate3d(${this.baseX + this.gesture.delta}px, 0, 0)`;
        this.updateArc(this.cards, this.gesture.delta, false);
      },
      { passive: false, signal }
    );

    this.viewport.addEventListener('pointerup', (event) => this.finishGesture(event, false), { signal });
    this.viewport.addEventListener('pointercancel', (event) => this.finishGesture(event, true), { signal });
  }

  finishGesture(event, cancelled) {
    if (!this.gesture || event.pointerId !== this.gesture.pointerId) return;

    const gesture = this.gesture;
    this.gesture = null;
    try {
      this.viewport.releasePointerCapture(event.pointerId);
    } catch (_error) {}

    const elapsed = Math.max(1, performance.now() - gesture.startTime);
    const velocity = Math.abs(gesture.delta) / elapsed;
    const threshold = Math.min(64, Math.max(38, this.cardWidth * 0.18));
    const shouldMove = !cancelled && gesture.dragging && (Math.abs(gesture.delta) >= threshold || velocity > 0.45);

    if (gesture.dragging) this.suppressClickUntil = performance.now() + 450;
    this.resume('gesture');

    if (shouldMove) {
      const direction = gesture.delta < 0 ? 1 : -1;
      if (!this.move(direction, true, gesture.delta)) this.snapTrackBack();
    } else {
      this.snapTrackBack();
    }
  }

  snapTrackBack() {
    if (!this.track) return;
    const snapDuration = Math.min(280, this.duration);
    this.track.style.transition = `transform ${snapDuration}ms cubic-bezier(0.22, 1, 0.36, 1)`;
    this.track.style.transform = `translate3d(${this.baseX}px, 0, 0)`;
    this.updateArc(this.cards, 0, true);
    if (this.snapTimer) window.clearTimeout(this.snapTimer);
    this.snapTimer = window.setTimeout(() => {
      this.track.style.removeProperty('transition');
      this.cards.forEach((card) => card.style.removeProperty('transition-duration'));
      this.snapTimer = undefined;
    }, snapDuration);
  }

  pause(reason = 'interaction') {
    this.pauseReasons.add(reason);
    this.clearTimer();
  }

  resume(reason = 'interaction') {
    this.pauseReasons.delete(reason);
    this.schedule();
  }

  get isPaused() {
    return this.pauseReasons.size > 0;
  }

  schedule() {
    this.clearTimer();
    if (this.reduceMotion || this.isPaused || !this.isSectionVisible || this.cards.length < 2 || this.isAnimating) return;
    const delay = Math.max(800, this.interval - this.duration);
    this.timer = window.setTimeout(() => this.advance(), delay);
  }

  clearTimer() {
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = undefined;
  }
}

if (!customElements.get('sk-reel-carousel')) {
  customElements.define('sk-reel-carousel', SkReelCarousel);
}
